import React from 'react';
import { CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const PostAwardServicesPage = () => {
  return (
    <div className="min-h-screen pt-20">
      <section className="relative h-80 flex items-center justify-center overflow-hidden bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900">
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Post-Award Services</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Transforming commitments into executable engineering deliverables
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Post-Award Services for <span className="text-red-600">Oil & Gas Engineering Projects</span>
            </h2>
            <div className="w-24 h-1 bg-red-600 mb-8" />
            <p className="text-lg text-slate-700 leading-relaxed">
              Post award engineering ensures safe, compliant, and efficient execution of upstream, midstream, and downstream 
              projects. It integrates multidisciplinary engineering, vendor coordination, project controls, and construction 
              support to deliver the project as per client expectations, regulatory requirements, and industry standards.
            </p>
          </div>

          {[
            {
              title: 'Engineering Mobilization and Project Setup',
              description: 'Once the award is confirmed, the engineering team establishes the foundation for execution.',
              items: [
                'Mobilization of discipline engineers and project support teams',
                'Kickoff meetings with client, licensors, and stakeholders',
                'Development of project execution plans, engineering plans, and quality plans',
                'Setup of EDMS/PMIS systems (WrenchSP, SharePoint)',
                'Establishment of communication protocols and interface management procedures'
              ]
            },
            {
              title: 'Detailed Engineering and Design Development',
              description: 'This is the core of post award engineering, where all technical deliverables are produced.',
              items: [
                'Development of PFDs, P&IDs, equipment datasheets, and design calculations',
                '3D modeling (PDMS/E3D/SP3D) and clash detection',
                'Layout engineering for process, piping, electrical, instrumentation, and civil/structural',
                'Stress analysis, hydraulic studies, HAZOP/HAZID actions closure',
                'Preparation of IFC (Issued for Construction) deliverables',
                'Compliance with ADNOC, Aramco, and international standards'
              ]
            },
            {
              title: 'Procurement Engineering and Vendor Coordination',
              description: 'Vendor engagement is critical in oil & gas projects due to long lead equipment and technical complexity.',
              items: [
                'Preparation of technical requisitions and bid evaluation reports',
                'Review and approval of vendor documents and drawings',
                'Coordination with OEMs for design alignment and technical clarifications',
                'Inspection and FAT support for critical equipment',
                'Expediting to ensure timely delivery of materials'
              ]
            },
            {
              title: 'Project Controls and Progress Monitoring',
              description: 'Strong project controls ensure the project stays on schedule and within budget.',
              items: [
                'Development of Level 3/4 schedules and progress measurement systems',
                'Monitoring engineering deliverables, procurement milestones, and construction readiness',
                'Cost control, forecasting, and change management',
                'Weekly and monthly reporting to client and management',
                'Risk tracking and mitigation planning'
              ]
            },
            {
              title: 'Construction and Site Support',
              description: 'Engineering teams support construction to ensure design intent is maintained.',
              items: [
                'Response to site technical queries (TQs) and engineering clarifications',
                'Support for construction sequencing and work front planning',
                'As built documentation and red mark updates',
                'Resolution of field issues, clashes, and unforeseen conditions',
                'Support for pre commissioning and commissioning teams'
              ]
            },
            {
              title: 'Quality Assurance and Compliance',
              description: 'Oil & gas projects require strict adherence to quality and regulatory frameworks.',
              items: [
                'Implementation of engineering quality plans and ITPs',
                'Internal and external audits',
                'Compliance with ADNOC, Aramco, and local regulations',
                'Technical integrity checks and design assurance reviews'
              ]
            },
            {
              title: 'Interface and Stakeholder Management',
              description: 'Large oil & gas projects involve multiple contractors, licensors, and authorities.',
              items: [
                'Coordination with licensors, PMC, EPC contractors, and third party consultants',
                'Interface management across disciplines and packages',
                'Alignment with client expectations through regular reviews and workshops',
                'Management of regulatory submissions and approvals'
              ]
            },
            {
              title: 'Commissioning and Handover Support',
              description: 'The final stage ensures the facility is ready for safe operation.',
              items: [
                'Support for commissioning procedures and system readiness',
                'Review of test packs, loop checks, and pre start up activities',
                'Compilation of final documentation, O&M manuals, and as built packages',
                'Technical support during performance testing and start up'
              ]
            }
          ].map((section, idx) => (
            <div key={idx} className="mb-16">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">{section.title}</h3>
              <p className="text-slate-700 mb-6">{section.description}</p>
              <div className="grid md:grid-cols-2 gap-4">
                {section.items.map((item, index) => (
                  <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700 text-base">{item}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl font-bold text-slate-900 mb-6">Why Post-Award Services Matter in Oil & Gas</h3>
            <p className="text-lg text-slate-700 leading-relaxed mb-8">
              Post award engineering determines the success of the entire project lifecycle. It ensures:
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                'Safe and compliant design',
                'On time delivery of engineering and procurement packages',
                'Reduced rework and construction delays',
                'Strong coordination between engineering, procurement, and construction',
                'Smooth transition to commissioning and operations'
              ].map((item, index) => (
                <Card key={index} className="border-none shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base font-medium">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PostAwardServicesPage;
